/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing legacy code */
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import closeIcon from "assets/images/render-modal/close.svg";
import downloadIcon from "assets/images/render-modal/download.svg";
import nextIcon from "assets/images/render-modal/next.svg";
import pauseIcon from "assets/images/render-modal/pause.svg";
import playIcon from "assets/images/render-modal/play.svg";
import prevIcon from "assets/images/render-modal/prev.svg";
import stepPdfIcon from "assets/images/render-modal/step-pdf.svg";
import { StuffingCalculationLoader } from "components/Loading/StuffingCalculationLoader";
import loaderStyles from "components/Loading/StuffingCalculationLoader.module.scss";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { ImageContext } from "pages/StuffingPage";
import { ResponseContainer, RootState } from "store";
import { exportStuffingPdf } from "utils/exportStuffingPdf";
import { getProductsValues, typeToString } from "utils/utils";
import { RenderModalFooter } from "./RenderModalFooter/RenderModalFooter";
import WebGl, { WebGlHandle } from "./WebGl";
import styles from "./RenderModal.module.scss";

// Fixed render resolution for the 3D viewport — the canvas no longer
// tracks its container's size, so orbit/zoom framing stays consistent
// regardless of viewport/window size.
const CANVAS_WIDTH = 860;
const CANVAS_HEIGHT = 580;

interface RenderModalProps {
  closeModal: () => void;
  responseData: ResponseContainer;
  cargoesAttributes: any;
}

export const RenderModal = ({ closeModal, responseData, cargoesAttributes }: RenderModalProps) => {
  const [showCanvas, setShowCanvas] = useState(false);
  const productData = getProductsValues(responseData.general.items, cargoesAttributes);
  const totalSteps = Math.max(responseData.spaces?.length ?? 0, 1);
  const [currentStep, setCurrentStep] = useState(totalSteps);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStepPdfLoading, setIsStepPdfLoading] = useState(false);
  const [selectedCargoIds, setSelectedCargoIds] = useState(productData.map((prod: any) => prod.uid));

  const images = useContext(ImageContext);
  const { options, response, auth } = useSelector((state: RootState) => state.root);
  const { t } = useTranslation();
  const childRef = useRef<WebGlHandle>(null);
  const stepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentStepRef = useRef(currentStep);

  currentStepRef.current = currentStep;

  const isBusy = isStepPdfLoading;
  const progressPercent = Math.min(100, Math.round((currentStep / totalSteps) * 100));

  const volumeValue =
    (responseData.general.cargoVolume / (options.lengthUnits === "mm" ? 1000000000 : 1)).toFixed(2) +
    (options.lengthUnits === "mm" ? " m3" : ` ${options.lengthUnits}3`);
  const weightValue = `${responseData.general.cargoWeight.toFixed(0)} ${options.weightUnits === "lb" ? "lb" : "kg"}`;

  const clearStepTimer = () => {
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }
  };

  const applyStep = (step: number) => {
    const next = Math.max(0, Math.min(step, totalSteps));
    setCurrentStep(next);
    currentStepRef.current = next;
    childRef.current?.goToStep(next);
    return next;
  };

  useEffect(() => {
    // One rAF is enough — the canvas is a fixed CANVAS_WIDTH x CANVAS_HEIGHT
    // resolution, it no longer needs to wait on a container measurement.
    const frame = requestAnimationFrame(() => setShowCanvas(true));

    return () => {
      cancelAnimationFrame(frame);
      clearStepTimer();
    };
  }, []);

  const startProgressTimer = (fromStep: number) => {
    clearStepTimer();
    let step = fromStep;
    stepTimerRef.current = setInterval(() => {
      if (step >= totalSteps) {
        clearStepTimer();
        setIsPlaying(false);
        return;
      }
      step += 1;
      applyStep(step);
      if (step >= totalSteps) {
        clearStepTimer();
        setIsPlaying(false);
      }
    }, 1000);
  };

  const handlePlay = () => {
    if (isBusy) return;
    if (isPlaying) {
      clearStepTimer();
      setIsPlaying(false);
      return;
    }

    const startAt = currentStepRef.current >= totalSteps ? 0 : currentStepRef.current;
    if (startAt === 0 && currentStepRef.current !== 0) {
      applyStep(0);
    }
    setIsPlaying(true);
    startProgressTimer(startAt);
  };

  const handlePrev = () => {
    if (isBusy) return;
    clearStepTimer();
    setIsPlaying(false);
    applyStep(currentStepRef.current - 1);
  };

  const handleNext = () => {
    if (isBusy) return;
    clearStepTimer();
    setIsPlaying(false);
    applyStep(currentStepRef.current + 1);
  };

  const handleDownloadStuffingResult = () => {
    if (!response?.data?.result || isBusy) return;

    void exportStuffingPdf({
      result: response.data.result as ResponseContainer[],
      cargoAttributes: response.data.cargoAttributes ?? cargoesAttributes,
      images,
      options,
      user: auth?.user,
      containerUid: responseData.uid,
    });
  };

  const handleStepPdf = () => {
    if (isBusy) return;

    setIsStepPdfLoading(true);

    void (async () => {
      // Let the overlay paint before heavy WebGL/PDF work blocks the main thread.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      try {
        await childRef.current?.createStoryboard();
      } finally {
        setIsStepPdfLoading(false);
      }
    })();
  };

  return (
    <div className={styles.modal}>
      <header className={styles.header}>
        <h2 className={styles.title}>{typeToString(responseData.attr.type)}</h2>
        <button type="button" className={styles.closeBtn} aria-label={t("Close")} onClick={closeModal}>
          <img src={closeIcon} alt="" width={20} height={20} />
        </button>
      </header>

      <div className={styles.viewport}>
        <button
          type="button"
          className={styles.downloadBtn}
          aria-label={t("STUFFING RESULT")}
          disabled={isBusy}
          onClick={handleDownloadStuffingResult}
        >
          <img src={downloadIcon} alt="" width={16} height={16} />
          <span>{t("STUFFING RESULT")}</span>
        </button>

        <div className={styles.statsCard}>
          <div className={styles.statsRow}>
            <p className={styles.statsLabel}>{t("Cargo volume")}</p>
            <p className={styles.statsLabel}>{t("Cargo weight")}</p>
          </div>
          <div className={styles.statsRow}>
            <p className={styles.statsValue}>{volumeValue}</p>
            <p className={styles.statsValue}>{weightValue}</p>
          </div>
        </div>

        <div className={styles.canvasHost}>
          {showCanvas && (
            <WebGl ref={childRef} height={CANVAS_HEIGHT} width={CANVAS_WIDTH} responseData={responseData} />
          )}
        </div>

        <div className={styles.controls}>
          <div className={styles.controlGroup}>
            <button
              type="button"
              className={styles.controlBtn}
              aria-label={isPlaying ? t("Pause") : t("Play")}
              disabled={isBusy}
              onClick={handlePlay}
            >
              <img src={isPlaying ? pauseIcon : playIcon} alt="" width={40} height={40} />
            </button>
            <button
              type="button"
              className={styles.controlBtn}
              aria-label={t("Previous")}
              disabled={isBusy}
              onClick={handlePrev}
            >
              <img src={prevIcon} alt="" width={40} height={40} />
            </button>
            <button
              type="button"
              className={styles.controlBtn}
              aria-label={t("Next")}
              disabled={isBusy}
              onClick={handleNext}
            >
              <img src={nextIcon} alt="" width={40} height={40} />
            </button>
          </div>

          <div className={styles.progress} aria-hidden>
            <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
            <span className={styles.progressThumb} style={{ left: `${progressPercent}%` }} />
          </div>

          <p className={styles.stepCount}>
            {currentStep}/{totalSteps}
          </p>

          <Button
            type="button"
            variant={BUTTON_VARIANT.PRIMARY}
            className={styles.stepPdfBtn}
            disabled={isBusy}
            onClick={handleStepPdf}
          >
            <img src={stepPdfIcon} alt="" width={16} height={16} />
            {t("Step PDF")}
          </Button>
        </div>
      </div>

      <div className={styles.legend}>
        <RenderModalFooter
          containerVolume={responseData.attr.volume}
          productData={productData}
          selectedCargoIds={selectedCargoIds}
          setSelectedCargoIds={setSelectedCargoIds}
        />
      </div>

      {isStepPdfLoading && (
        <div className={styles.loadingOverlay} role="status" aria-live="polite">
          <div className={loaderStyles.modal}>
            <StuffingCalculationLoader
              title={t("Preparing Step PDF")}
              description={t("Generating step-by-step loading instructions")}
            />
          </div>
        </div>
      )}
    </div>
  );
};
